import {
  Button,
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  HoverableLinkSizes,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectPostingVirtualCardFromState,
  selectTopLevelErrorFromState,
} from 'src/app/CreateVirtualCardForm/redux/createVirtualCardForm.selectors'
import ErrorList from 'src/components/ErrorList/ErrorList'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { OrganizationRoles, svgImageNames } from 'src/enums'
import { useColorOrGrayscaleSVG } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import styled, { css, CSSProp } from 'styled-components'

interface ChooseRecipientMembershipPopupProps {
  selectedEmailsNotInOrg: string[]
  submitFunction: (role?: OrganizationRoles) => void
  closeFunction: (val: boolean) => void
}

const StyledDiv = styled.div<{
  isSelected?: boolean
}>`
  border-radius: 10px;
  background-color: ${styleConstants.colors.gray_light};
  display: flex;
  padding: 20px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  gap: 12px;
  cursor: pointer;
  ${({ isSelected }): CSSProp => {
    return isSelected
      ? css`
          border: 1px solid ${themeConstants.colors.cta};
        `
      : css`
          border: 1px solid ${styleConstants.colors.gray_medium};
        `
  }}
`
const ChooseRecipientMembershipPopup: React.FC<
  ChooseRecipientMembershipPopupProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const postingVirtualCard = useSelector(selectPostingVirtualCardFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const numberSelectedEmailsNotInOrg = props.selectedEmailsNotInOrg.length
  const plural = numberSelectedEmailsNotInOrg > 1
  const appName = useSelector(selectAppName)
  const buildingsPeople = useColorOrGrayscaleSVG(svgImageNames.BuildingsPeople)
  const profile = useColorOrGrayscaleSVG(svgImageNames.InviteGuest)

  const [selectedRole, setSelectedRole] = useState<OrganizationRoles>(
    OrganizationRoles.member,
  )

  return (
    <Popup
      css={css`
        display: flex;
        flex-direction: column;
        z-index: 1;
        width: 770px;
      `}
      onClickClose={(): void => {
        props.closeFunction(false)
      }}
      showCloseButton
    >
      <div
        css={css`
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate(
          plural
            ? 'Assign a role to the card recipients'
            : 'Assign a role to the card recipient',
        )}
      </div>
      <div
        css={css`
          margin-top: 12px;
          display: inline;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          plural
            ? 'The recipients are not in your organization. Before you can send your virtual cards, you must either invite them as Members or add them as Guests. Learn more about roles in our ${resourceCenter}.'
            : 'The recipient is not in your organization. Before you can send your virtual card, you must either invite them as a Member or add them as a Guest. Learn more about roles in our ${resourceCenter}.',
          {
            resourceCenter: (
              <ResourceCenterLink
                size={HoverableLinkSizes.Large}
                underline
                regular
              />
            ),
          },
        )}
      </div>
      <div
        css={css`
          margin-top: 12px;
          display: inline;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          plural
            ? "Please select the recipients' role below"
            : "Please select the recipient's role below",
        )}
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 20px;
          align-self: stretch;
          margin-top: 20px;
        `}
      >
        <StyledDiv
          onClick={(): void => setSelectedRole(OrganizationRoles.member)}
          isSelected={selectedRole === OrganizationRoles.member}
        >
          {buildingsPeople}
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translate('Member')}
          </span>
          <span
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              "The default user role. For employees who need to make payments, but don't have administrative needs.",
            )}
          </span>
          <span
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              'Members can view their virtual cards in ${brandName}. They can also request updates and new cards.',
              {
                brandName: appName,
              },
            )}
          </span>
        </StyledDiv>
        <StyledDiv
          onClick={(): void => setSelectedRole(OrganizationRoles.guest)}
          isSelected={selectedRole === OrganizationRoles.guest}
        >
          {profile}
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translate('Guest')}
          </span>
          <span
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate('For contractors and temporary employees.')}
          </span>
          <span
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              'Guests receive a secure link to cards from an email or can use a lite version of ${brandName}. Guests cannot request updates or new cards.',
              {
                brandName: appName,
              },
            )}
          </span>
        </StyledDiv>
      </div>
      {topLevelError !== undefined && <ErrorList errors={[topLevelError]} />}
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-top: 40px;
          gap: 20px;
        `}
      >
        <Button
          type={ButtonTypes.submit}
          design={ButtonDesigns.designCta}
          big
          text={translate('Send Virtual Card and Assign Role')}
          onClick={(): void => props.submitFunction(selectedRole)}
          loading={postingVirtualCard}
        />
        <span
          role="link"
          tabIndex={0}
          onClick={(): void => {
            if (!postingVirtualCard) props.closeFunction(false)
          }}
          onKeyDown={undefined}
          // TODO: create a hoverable link design for the red styling
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.cta,
              styleConstants.fontFamilies.medium,
            )};
            &:hover {
              ${!postingVirtualCard &&
              css`
                text-decoration: underline;
                cursor: pointer;
              `}
            }
          `}
          data-testid="MembershipType-CancelButton"
        >
          {translate('Cancel')}
        </span>
      </div>
    </Popup>
  )
}

export default ChooseRecipientMembershipPopup
